import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const projectDetails = {
  'timed-trials': {
    title: 'Timed Trials | Motivosity',
    shortDescription: 'Led the creation of a Timed Trials solution, revolutionizing how customers trial add-ons.',
    overview: `At Motivosity, I took a role in spearheading a groundbreaking project focused on the integration of a Timed Trials solution within our software. With a primary goal of empowering existing customers, my responsibilities included conceptualizing and implementing a seamless user experience. I led the refinement of the sign-up process, orchestrated dynamic page displays enriched with a user-friendly 30-day trial countdown, and introduced a robust feedback system. Additionally, I crafted a self-service flow facilitating contract upgrades when a trial had ended.

In addition to the work on our software product, my responsibilities also included adding trial information to our internal tool. This allowed any department at Motivosity to see the status of any trial(s) for a company, view their feedback, and see what expansion deals were a direct result of the trial implementation.`,
    techOverview: `This project leveraged key technologies to ensure a cutting-edge solution. The frontend was developed using Angular, providing a robust and scalable user interface. Styling was implemented using SCSS to achieve a modern and cohesive design. Testing was implemented with Karma and Jasmine ensuring code quality and reliability. For state management, NGRX Store was employed, enhancing data flow and maintaining a consistent application state. This tech stack not only facilitated the seamless implementation of project features but also laid the foundation for a resilient and forward-looking software architecture. As a result of these advancements, the project contributed significantly to an observable increase in expansion deals, underlining its strategic impact on both user satisfaction and business growth.`,
    tech: ['Angular 2', 'TypeScript', 'SCSS', 'Karma', 'Jasmine', 'NGRX Store', 'Java'],
    images: ['/images/timed-trials/1.png', '/images/timed-trials/2.png', '/images/timed-trials/3.png', '/images/timed-trials/4.png'],
  },
  'motivosity-website': {
    title: 'React Website | Motivosity',
    shortDescription: 'Created a complete website transformation for Motivosity dedicated to marketing and client interactions.',
    overview: `As the sole developer, I took the helm in creating a transformative new website for Motivosity, dedicated to marketing and client interactions. This ground-up initiative involved a complete redesign, shaping Motivosity's brand identity through strategic collaboration with a copywriter and a UI designer. Motivosity.com includes essential pages and features, including a comprehensive product overview, resource center, dynamic careers page, conversion-centric landing pages, and a pricing section complete with custom calculators. Constant enhancements through A/B testing ensure an optimal user experience. The website not only adheres to Google's web standards but also ensures a great user experience because of responsive design and mobile optimizations.`,
    techOverview: `I orchestrated the creation of the static website using React, Angular, and GraphQL, ensuring a robust and versatile architecture. The choice of AWS Amplify for CI/CD pipeline and deployment streamlined the development process. Integrations with New Relic and HubSpot, coupled with CSS animations, enriched the user interface and elevated performance. This tech-driven solution is a testament to my ability to lead and execute a project successfully, collaborating seamlessly with a team.

Since this website was built with React & Gatsby, I leveraged key React features such as component-based architecture, lifecycle hooks, virtual DOM for optimal performance, and reusable components for a scalable and maintainable codebase. Communication of technical aspects with stakeholders was a priority, ensuring a shared understanding of the project's intricacies and fostering a collaborative environment for success.`,
    tech: ['React', 'Gatsby', 'GraphQL', 'AWS Amplify', 'SCSS', 'New Relic', 'HubSpot'],
    images: ['/images/motivosity-website/2.png', '/images/motivosity-website/3.png', '/images/motivosity-website/4.png', '/images/motivosity-website/5.png', '/images/motivosity-website/10.png'],
    link: 'https://www.motivosity.com',
  },
  'health-score': {
    title: 'Company Health Scores | Motivosity',
    shortDescription: 'Backend integration for generating internal health scores to provide insights into customer status.',
    overview: `As the backend engineer for this project, I integrated Java, AngularJS, and SQL to efficiently generate internal health scores to provide valuable insights into our customers. This resulted in customer success allowing swift assessments of company status and proactive engagement with at-risk clients. The Health Score system became a crucial tool for our customer success team to identify at-risk accounts and take preventive action.`,
    techOverview: `This project demonstrates full-stack integration capabilities. The backend was built with Java, providing robust processing for complex calculations. The frontend interface was developed with AngularJS for a responsive user experience. SQL databases were optimized for efficient querying of large datasets. The system processes real-time customer data to generate actionable insights for our customer success team.`,
    tech: ['Java', 'AngularJS', 'SQL', 'Backend Development', 'API Design'],
    images: ['/images/health-score/2.png', '/images/health-score/1.png'],
  },
  'advisor-app': {
    title: 'Financial Advisor Mobile App',
    shortDescription: 'Angular & Ionic mobile app providing financial advisors with tools to manage client portfolios.',
    overview: `This Angular & Ionic mobile app provided financial advisors with a powerful tool to efficiently manage client portfolios. The application streamlined complex financial workflows and provided real-time access to portfolio data, enabling advisors to make informed decisions on the go.`,
    techOverview: `Built with Angular and Ionic, this cross-platform mobile application ensures consistency across iOS and Android devices. The app leverages native capabilities through Ionic plugins while maintaining a shared codebase. TypeScript ensures type safety throughout the application, and local storage capabilities allow offline access to critical advisor data.`,
    tech: ['Angular', 'Ionic', 'TypeScript', 'Mobile Development', 'Cross-platform'],
    images: ['/images/advisor-app/1.png'],
  },
  'encircle-app': {
    title: 'Encircle React Native App',
    shortDescription: 'Mobile app delivering feature-rich services for the LGBT+ community.',
    overview: `This project reflects my proficiency in mobile app development, combining technology stacks to deliver a feature-rich application in support of Encircle's invaluable services for the LGBT+ community. The app provides accessible tools and resources that connect users with essential support services.`,
    techOverview: `Developed with React Native, this application demonstrates cross-platform mobile development expertise. The app integrates with Firebase for real-time data synchronization and user authentication. Push notifications keep users informed of important updates and resources. The responsive design ensures excellent user experience across various device sizes.`,
    tech: ['React Native', 'JavaScript', 'Firebase', 'Mobile Development', 'Cross-platform'],
    images: ['/images/encircle-app/2.png'],
  },
  'abgc-website': {
    title: 'CMS Corporate Website',
    shortDescription: 'Corporate website redesign built with a popular CMS for easy resource creation.',
    overview: `This corporate website redesign was built with a popular CMS to allow non-technical users easy resource creation. The intuitive interface empowered team members to create, edit, and publish content without requiring technical expertise.`,
    techOverview: `Leveraging a robust CMS platform, this solution prioritizes user-friendliness and maintainability. The responsive design ensures the website functions seamlessly across all devices. Custom templates and workflows were configured to align with organizational processes and branding guidelines.`,
    tech: ['CMS', 'HTML', 'CSS', 'Responsive Design', 'Web Development'],
    images: ['/images/abgc-website/1.png'],
  },
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const project = projectDetails[id];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) {
    return (
      <div className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Project Not Found</h1>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Portfolio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 hover:text-blue-800 mb-4 flex items-center gap-2"
          >
            ← Back to Portfolio
          </button>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900">{project.title}</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Project Overview */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
          <div className="prose prose-lg max-w-none">
            {project.overview.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="text-gray-700 mb-4">
                {paragraph}
              </p>
            ))}
          </div>
        </section>

        {/* Tech Overview */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Tech Overview</h2>
          <div className="prose prose-lg max-w-none">
            {project.techOverview.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="text-gray-700 mb-4">
                {paragraph}
              </p>
            ))}
          </div>
        </section>

        {/* Technologies */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Technologies Used</h2>
          <div className="flex flex-wrap gap-3">
            {project.tech.map((tech, idx) => (
              <span
                key={idx}
                className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold"
              >
                {tech}
              </span>
            ))}
          </div>
        </section>

        {/* Images Gallery */}
        {project.images && project.images.length > 0 && (
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Showcase</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {project.images.map((img, idx) => (
                <div key={idx} className="rounded-lg overflow-hidden shadow-lg">
                  <img src={img} alt={`Project screenshot ${idx + 1}`} className="w-full h-auto" />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* External Link */}
        {project.link && (
          <section className="mb-12">
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition font-semibold"
            >
              View Live Project →
            </a>
          </section>
        )}
      </div>
    </div>
  );
}
